import { useEffect, useState } from "react";
import api from "../../lib/api";
import { TrendingUp, Building2, Users, Wallet, Star, Bed } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from "recharts";

export default function PropertyAnalyticsTab() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/owner/property-analytics").then(r => setData(r.data)).catch(() => setData({ properties: [], totals: {} }));
  }, []);
  if (!data) return <div>Loading…</div>;
  const t = data.totals || {};

  return (
    <div data-testid="property-analytics-tab">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        <KPI label="Total Beds" value={t.total_beds || 0} icon={<Bed size={14}/>} color="lime"/>
        <KPI label="Occupied" value={`${t.occupied_beds || 0}`} sub={`${t.overall_occupancy_pct || 0}%`} icon={<TrendingUp size={14}/>} color="pink"/>
        <KPI label="Enquiries" value={t.total_enquiries || 0} icon={<Users size={14}/>} color="blue"/>
        <KPI label="Paid Bookings" value={t.total_bookings_paid || 0} sub={`${t.overall_conversion_pct || 0}% conv.`} icon={<Building2 size={14}/>} color="lime"/>
        <KPI label="Revenue" value={`₹${(t.total_revenue || 0).toLocaleString()}`} icon={<Wallet size={14}/>} color="white" highlight/>
      </div>

      {data.properties.length > 0 && (
        <div className="brut-card p-5 mb-6">
          <h3 className="font-display font-black text-2xl mb-3">Occupancy by property</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={data.properties.map(p => ({ name: p.title.slice(0, 16), Occupancy: p.occupancy_pct, Conversion: p.conversion_pct }))}>
              <CartesianGrid stroke="#0F1F17" strokeDasharray="3 3" opacity={0.2}/>
              <XAxis dataKey="name" tick={{ fontSize: 11 }}/>
              <YAxis unit="%"/>
              <Tooltip contentStyle={{ border: "2px solid #0F1F17", borderRadius: 0 }}/>
              <Bar dataKey="Occupancy" fill="#2ED573" stroke="#0F1F17" strokeWidth={1.5}>
                {data.properties.map((_, i) => <Cell key={i}/>)}
              </Bar>
              <Bar dataKey="Conversion" fill="#FF90E8" stroke="#0F1F17" strokeWidth={1.5}/>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      <div className="brut-card overflow-x-auto" data-testid="prop-analytics-table">
        <table className="w-full text-sm">
          <thead className="bg-[#ECFAF1] border-b-2 border-[#0F1F17]">
            <tr>
              <th className="p-3 text-left">Property</th>
              <th className="p-3 text-left">Status</th>
              <th className="p-3 text-right">Beds</th>
              <th className="p-3 text-right">Occupancy</th>
              <th className="p-3 text-right">Enquiries</th>
              <th className="p-3 text-right">Paid</th>
              <th className="p-3 text-right">Conv.</th>
              <th className="p-3 text-right">Revenue</th>
              <th className="p-3 text-right">Rating</th>
            </tr>
          </thead>
          <tbody>
            {data.properties.map(p => (
              <tr key={p.property_id} className="border-b border-[#0F1F17]/20" data-testid={`prop-${p.property_id}`}>
                <td className="p-3"><div className="font-bold">{p.title}</div><div className="text-xs text-[#484848]">{p.city}</div></td>
                <td className="p-3"><span className={`brut-tag ${p.status === "active" || p.status === "approved" ? "lime" : "pink"} !text-[10px]`}>{p.status}</span></td>
                <td className="p-3 text-right">{p.occupied_beds}/{p.total_beds}</td>
                <td className="p-3 text-right">
                  <div className="flex items-center gap-2 justify-end">
                    <div className="bg-[#ECFAF1] brut-border h-3 w-20 relative overflow-hidden">
                      <div className="absolute inset-y-0 left-0 bg-[#2ED573]" style={{ width: `${p.occupancy_pct}%` }}/>
                    </div>
                    <strong className="text-xs w-10 text-right">{p.occupancy_pct}%</strong>
                  </div>
                </td>
                <td className="p-3 text-right">{p.enquiries}</td>
                <td className="p-3 text-right">{p.paid_bookings}/{p.bookings}</td>
                <td className="p-3 text-right"><strong className={p.conversion_pct >= 30 ? "text-[#2ED573]" : ""}>{p.conversion_pct}%</strong></td>
                <td className="p-3 text-right font-bold">₹{p.revenue.toLocaleString()}</td>
                <td className="p-3 text-right"><span className="inline-flex items-center gap-1"><Star size={12} className="fill-[#FFC857]"/>{p.avg_rating || "—"}</span></td>
              </tr>
            ))}
            {data.properties.length === 0 && <tr><td colSpan="9" className="p-8 text-center text-[#484848]">List your first property to see analytics.</td></tr>}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-[#484848] mt-3">Occupancy = occupied beds / total beds. Conversion = paid bookings / enquiries.</p>
    </div>
  );
}

function KPI({ label, value, sub, icon, color, highlight }) {
  return (
    <div className={`brut-card p-4 ${highlight ? "bg-[#2ED573]" : ""}`}>
      <div className={`brut-tag ${color} !text-[10px] flex items-center gap-1 w-fit`}>{icon}{label}</div>
      <div className="font-display font-black text-2xl md:text-3xl mt-2">{value}</div>
      {sub && <div className="text-xs text-[#484848] mt-1">{sub}</div>}
    </div>
  );
}
